import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, lastDayOfMonth, setDate } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';

interface GenerateOptions {
  month: number; // 1-12
  year: number;
  includeRecurring: boolean;
  includePayroll: boolean;
  includeFixedOnly?: boolean;
  includeVariableOnly?: boolean;
}

interface GeneratedExpense {
  business_unit: string;
  description: string;
  amount: number;
  currency: string;
  due_date: string;
  notes?: string;
  recurrence: string;
  status: string;
  source_type: string;
  source_id: string;
  is_estimate: boolean;
  budgeted_amount?: number;
  account_code?: string;
}

export function useGenerateMonthlyExpenses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: GenerateOptions) => {
      const { month, year, includeRecurring, includePayroll } = options;
      const expenses: GeneratedExpense[] = [];

      // Helper to create due date for the month
      const createDueDate = (day: number): string => {
        const baseDate = new Date(year, month - 1, 1);
        const lastDay = lastDayOfMonth(baseDate).getDate();
        const actualDay = Math.min(day, lastDay);
        return safeFormat(setDate(baseDate, actualDay), 'yyyy-MM-dd');
      };

      // 1. Get recurring expenses
      if (includeRecurring) {
        const { data: recurringExpenses, error: recurringError } = await supabase
          .from('recurring_expenses')
          .select('*')
          .eq('is_active', true);

        if (recurringError) throw recurringError;

        for (const recurring of recurringExpenses || []) {
          const businessUnit = recurring.business_unit || 'us';
          const dueDay = recurring.day_of_month || 15;
          const isVariable = recurring.expense_type === 'variable' || recurring.expense_type === 'mixed';
          const isEstimate = isVariable && recurring.needs_invoice;

          expenses.push({
            business_unit: businessUnit,
            description: recurring.name,
            amount: recurring.amount,
            currency: recurring.currency || (businessUnit === 'br' ? 'BRL' : 'USD'),
            due_date: createDueDate(dueDay),
            notes: isEstimate 
              ? `⚠️ Valor estimado - Aguardando fatura. Origem: ${recurring.name}` 
              : `Gerado de despesa recorrente: ${recurring.name}`,
            recurrence: 'monthly',
            status: 'pending',
            source_type: 'recurring',
            source_id: recurring.id,
            is_estimate: isEstimate,
            budgeted_amount: recurring.budget_amount,
            account_code: recurring.account_code,
          });
        }
      }

      // 2. Get employees for payroll
      if (includePayroll) {
        const { data: employees, error: employeesError } = await supabase
          .from('employees')
          .select('*')
          .eq('status', 'active');

        if (employeesError) throw employeesError;

        for (const employee of employees || []) {
          const businessUnit = employee.business_unit || 'us';
          const payrollDay = 5;
          const totalCost = (employee.base_salary || 0) + (employee.benefits_cost || 0);

          if (totalCost > 0) {
            expenses.push({
              business_unit: businessUnit,
              description: `Salário - ${employee.full_name}`,
              amount: totalCost,
              currency: employee.salary_currency || (businessUnit === 'br' ? 'BRL' : 'USD'),
              due_date: createDueDate(payrollDay),
              notes: `Folha de pagamento: ${employee.full_name} (${employee.role || 'Colaborador'})`,
              recurrence: 'monthly',
              status: 'pending',
              source_type: 'payroll',
              source_id: employee.id,
              is_estimate: false,
              account_code: employee.account_code || undefined,
            });
          }
        }
      }

      if (expenses.length === 0) {
        return { inserted: 0, skipped: 0 };
      }

      // Check for existing expenses to avoid duplicates
      const monthStart = safeFormat(new Date(year, month - 1, 1), 'yyyy-MM-dd');
      const monthEnd = safeFormat(lastDayOfMonth(new Date(year, month - 1, 1)), 'yyyy-MM-dd');

      const { data: existingExpenses } = await supabase
        .from('expenses')
        .select('description, due_date')
        .gte('due_date', monthStart)
        .lte('due_date', monthEnd);

      const existingSet = new Set(
        (existingExpenses || []).map(e => `${e.description}|${e.due_date}`)
      );

      // Filter out duplicates
      const newExpenses = expenses.filter(
        e => !existingSet.has(`${e.description}|${e.due_date}`)
      );

      const skipped = expenses.length - newExpenses.length;

      if (newExpenses.length === 0) {
        return { inserted: 0, skipped };
      }

      // Insert new expenses
      const { error: insertError } = await supabase
        .from('expenses')
        .insert(newExpenses);

      if (insertError) throw insertError;

      return { inserted: newExpenses.length, skipped };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

export interface PreviewItem {
  type: 'recurring' | 'payroll' | 'fixed' | 'variable';
  expenseType?: 'fixed' | 'variable' | 'mixed';
  description: string;
  amount: number;
  currency: string;
  dueDate: string;
  source: string;
  sourceId: string;
  isEstimate: boolean;
  needsInvoice?: boolean;
  accountCode?: string;
}

// Preview hook - returns what would be generated without inserting
export function usePreviewMonthlyExpenses() {
  return useMutation({
    mutationFn: async (options: Omit<GenerateOptions, 'includeRecurring' | 'includePayroll'> & { 
      includeRecurring?: boolean; 
      includePayroll?: boolean;
      includeFixed?: boolean;
      includeVariable?: boolean;
    }) => {
      const { 
        month, 
        year, 
        includeRecurring = true, 
        includePayroll = true,
        includeFixed = true,
        includeVariable = true,
      } = options;
      
      const preview: PreviewItem[] = [];

      const createDueDate = (day: number): string => {
        const baseDate = new Date(year, month - 1, 1);
        const lastDay = lastDayOfMonth(baseDate).getDate();
        const actualDay = Math.min(day, lastDay);
        return safeFormat(setDate(baseDate, actualDay), 'yyyy-MM-dd');
      };

      if (includeRecurring) {
        const { data: recurringExpenses } = await supabase
          .from('recurring_expenses')
          .select('*')
          .eq('is_active', true);

        for (const recurring of recurringExpenses || []) {
          const expenseType = recurring.expense_type || 'fixed';
          const isVariable = expenseType === 'variable' || expenseType === 'mixed';
          
          // Filter based on includeFixed/includeVariable
          if (!includeFixed && expenseType === 'fixed') continue;
          if (!includeVariable && isVariable) continue;
          
          preview.push({
            type: isVariable ? 'variable' : 'fixed',
            expenseType: expenseType as 'fixed' | 'variable' | 'mixed',
            description: recurring.name,
            amount: recurring.amount,
            currency: recurring.currency || (recurring.business_unit === 'br' ? 'BRL' : 'USD'),
            dueDate: createDueDate(recurring.day_of_month || 15),
            source: `Despesa ${expenseType === 'fixed' ? 'Fixa' : expenseType === 'variable' ? 'Variável' : 'Mista'}`,
            sourceId: recurring.id,
            isEstimate: isVariable && recurring.needs_invoice,
            needsInvoice: recurring.needs_invoice,
            accountCode: recurring.account_code,
          });
        }
      }

      if (includePayroll) {
        const { data: employees } = await supabase
          .from('employees')
          .select('*')
          .eq('status', 'active');

        for (const employee of employees || []) {
          const totalCost = (employee.base_salary || 0) + (employee.benefits_cost || 0);
          if (totalCost > 0) {
            preview.push({
              type: 'payroll',
              description: `Salário - ${employee.full_name}`,
              amount: totalCost,
              currency: employee.salary_currency || (employee.business_unit === 'br' ? 'BRL' : 'USD'),
              dueDate: createDueDate(5),
              source: `Funcionário: ${employee.role || 'Colaborador'}`,
              sourceId: employee.id,
              isEstimate: false,
              accountCode: employee.account_code,
            });
          }
        }
      }

      return preview;
    },
  });
}

// Get pending estimates that need real values
export function useEstimatedExpenses(month?: number, year?: number) {
  return useMutation({
    mutationFn: async () => {
      let query = supabase
        .from('expenses')
        .select('*')
        .eq('is_estimate', true)
        .eq('status', 'pending')
        .order('due_date', { ascending: true });

      if (month && year) {
        const monthStart = safeFormat(new Date(year, month - 1, 1), 'yyyy-MM-dd');
        const monthEnd = safeFormat(lastDayOfMonth(new Date(year, month - 1, 1)), 'yyyy-MM-dd');
        query = query.gte('due_date', monthStart).lte('due_date', monthEnd);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}
